import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { routes } from '../../shared/routes';
import { environment } from 'environment/environment';

/**
 * Ajoute l'access token aux requêtes. Sur un 401, le renouvelle une fois avec le refresh token (cookie
 * HttpOnly) et rejoue la requête ; si la session est terminée, renvoie vers la connexion.
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const toastr = inject(ToastrService);

    // Connexion, refresh, logout : authentifiés par le cookie. Un access token expiré y serait refusé (401).
    const isSessionCall = req.url.startsWith(`${environment.API_AUTH}/public/auth/`);
    // Sur les routes publiques (connexion, mot de passe oublié…), un 401 veut dire
    // « identifiants incorrects » : c'est la page qui l'affiche, ce n'est pas une session expirée.
    const isPublicAuthCall = req.url.startsWith(`${environment.API_AUTH}/public/`);
    // Les services authentifiés par clé d'application (template-service, X-API-Key) renvoient 401
    // quand la clé est refusée : la session utilisateur reste valide, c'est la page qui affiche l'erreur.
    const isApiKeyCall = req.headers.has('X-API-Key');

    const withToken = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
        const token = authService.token;
        return token && !isSessionCall
            ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
            : request;
    };

    return next(withToken(req)).pipe(
        catchError((error) => {
            const sessionMayHaveExpired = error instanceof HttpErrorResponse && error.status === 401
                && !isPublicAuthCall && !isApiKeyCall;
            if (!sessionMayHaveExpired) {
                return throwError(() => error);
            }
            return authService.refreshSession().pipe(
                catchError(() => {
                    // Prévenu une seule fois, même si plusieurs requêtes échouaient en même temps.
                    if (authService.handleSessionExpired()) {
                        toastr.warning(
                            'Votre session a expiré, veuillez vous reconnecter.',
                            'Déconnexion'
                        );
                        router.navigate([routes.signIn]).catch(() => {});
                    }
                    return throwError(() => error);
                }),
                switchMap(() => next(withToken(req)))
            );
        })
    );
};
